import { useState } from 'react';
import styles from './MoodRow.module.scss';
import { values } from '../CheckIn/checkInValues';
import ModalContainer from '../components/ModalContainer';
import ExtraCheckInModal from './ExtraCheckInModal';
import { JournalData, MiddleSchoolCheckInEntry } from '../../peekapak-types/ReportDataExchangeTypes';

interface Props {
  studentName: string;
  isShowStudents: boolean;
  data?: JournalData['Progress'];
  dateRange: Date[];
}

interface ModalContent {
  date: Date;
  checkIns: MiddleSchoolCheckInEntry[];
}

const MoodRow = ({ studentName, isShowStudents, data, dateRange }: Props): JSX.Element => {
  const [isShowModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    date: new Date(),
    checkIns: [],
  });

  const entriesFilteredByDate = data?.filter((item) => {
    const dateRangeCopy = dateRange.map((date) => new Date(date));
    const itemDate = new Date(item.createdAt);
    return (
      itemDate > dateRange[0] && itemDate < new Date(dateRangeCopy[dateRangeCopy.length - 1].setHours(23, 59, 59, 999))
    );
  });

  const checkInArray = entriesFilteredByDate
    ? dateRange.reduce(
        (acc, curr: Date, index) => {
          acc[index] =
            entriesFilteredByDate.filter((item) => {
              const itemDate = new Date(item.createdAt);
              if (index < dateRange.length - 1) return itemDate >= curr && itemDate < dateRange[index + 1];
              if (index === dateRange.length - 1) return itemDate > curr;
            }) || [];
          return acc;
        },
        [] as JournalData['Progress'][],
      )
    : new Array(dateRange.length).fill([]);

  const getTextColor = (feeling: string | undefined) => {
    if (feeling === undefined) return '#86939e';
    if (values.findIndex((value) => value.subEmotions.includes(feeling)) < 2) return '#43484d';
    return '#fff';
  };

  const hideModal = () => setShowModal(false);

  const populateModal = (checkIns: MiddleSchoolCheckInEntry[], date: Date) => {
    if (checkIns.length === 0) return;
    setModalContent({ date, checkIns });
    setShowModal(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.nameCell}>
        <h4>{isShowStudents ? studentName : 'Private'}</h4>
      </div>

      <div className={styles.unitDataContainer}>
        {checkInArray.map((checkInDay, index) => {
          if (checkInDay.length > 1) {
            checkInDay.sort((a: MiddleSchoolCheckInEntry, b: MiddleSchoolCheckInEntry) => b.createdAt - a.createdAt);
          }
          const emotionColor =
            values.find((value) => value.subEmotions.includes(checkInDay[0]?.feeling))?.segmentColor || '#bbc2ca';
          const hasData = checkInDay.length > 0;

          return (
            <div
              key={`${studentName}${index} activity.id`}
              className={`${styles.dataCell} ${hasData ? styles.dataCellClickable : ''}`}
              style={{ backgroundColor: emotionColor }}
              onClick={() => hasData && populateModal(checkInDay, dateRange[index])}
              role={hasData ? 'button' : undefined}
              tabIndex={hasData ? 0 : undefined}
              onKeyDown={(e) => {
                if (hasData && (e.key === 'Enter' || e.key === ' ')) populateModal(checkInDay, dateRange[index]);
              }}
            >
              <h4 style={{ color: getTextColor(checkInDay[0]?.feeling) }}>
                {checkInDay[0]?.feeling?.replace('/', ' / ') || '-'}
              </h4>

              {checkInDay.length > 1 && (
                <div className={styles.modalButtonContainer}>
                  <div className={styles.multiIndicator}>{checkInDay.length}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isShowModal && (
        <ModalContainer hideModal={hideModal}>
          <ExtraCheckInModal hideModal={hideModal} checkIns={modalContent.checkIns} date={modalContent.date} />
        </ModalContainer>
      )}
    </div>
  );
};

export default MoodRow;
